/**
 * Profile Dashboard Widget
 * Shows learning profile summary, courses, and insights on the dashboard
 */

import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { useLearningProfileContext } from '@/contexts/LearningProfileContext';
import { useProfileCourses, useProfileBeliefs, useRemoveCourse, useClearCourses } from '@/hooks/use-learning-profile';
import { slugToName } from '@/lib/subject-mapping';
import {
  Target,
  Plus,
  X,
  ChevronRight,
  Sparkles,
  GraduationCap,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Link } from 'react-router-dom';

interface ProfileDashboardWidgetProps {
  className?: string;
  showCourseManagement?: boolean;
  compact?: boolean;
}

export function ProfileDashboardWidget({
  className,
  showCourseManagement = true,
  compact = false,
}: ProfileDashboardWidgetProps) {
  const { profile, isLoading, profileCompleteness, triggerOnboarding } = useLearningProfileContext();
  const { data: courses = [], isLoading: coursesLoading } = useProfileCourses();
  const { data: weaknesses = [], isLoading: weaknessesLoading } = useProfileBeliefs('weakness');
  const removeCourse = useRemoveCourse();
  const clearCourses = useClearCourses();

  const statusDisplay = useMemo(() => {
    if (!profile) return null;

    const statusLabels: Record<string, string> = {
      '1L': '1L Student',
      '2L': '2L Student',
      '3L': '3L Student',
      'bar_prep': 'Bar Prep',
      'attorney': 'Attorney',
      'llm': 'LLM Student',
      'other': 'Legal Learner',
      'unknown': 'Getting Started',
    };

    const goalLabels: Record<string, string> = {
      'cold_calls': 'Cold Call Prep',
      'finals': 'Finals Prep',
      'bar': 'Bar Exam',
      'writing_improvement': 'Writing Skills',
      'fundamentals': 'Fundamentals',
      'general': 'General Learning',
    };

    return {
      status: statusLabels[profile.current_status] || profile.current_status,
      goal: goalLabels[profile.goal_mode] || profile.goal_mode,
    };
  }, [profile]);

  // Use unified subject mapping as single source of truth
  const getSubjectLabel = (value: string) => slugToName(value) || value;

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!profile) {
    return null;
  }

  // Show incomplete profile prompt
  if (profile.current_status === 'unknown' && !profile.onboarding_skipped) {
    return (
      <Card className={cn("border-primary/20 bg-primary/5", className)}>
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-full bg-primary/10">
              <Sparkles className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-lg">Personalize Your Experience</h3>
              <p className="text-muted-foreground text-sm mt-1">
                Tell us what you&apos;re studying and we&apos;ll tailor recommendations just for you.
              </p>
              <Button onClick={triggerOnboarding} className="mt-4" size="sm">
                Get Started
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <GraduationCap className="h-5 w-5" />
            Your Learning Profile
          </CardTitle>
          {profileCompleteness < 1 && (
            <Button variant="ghost" size="sm" onClick={triggerOnboarding}>
              Complete Profile
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Status & Goal Row */}
        <div className="flex items-center gap-3 text-sm">
          <Badge variant="secondary" className="font-medium">
            {statusDisplay?.status}
          </Badge>
          {statusDisplay?.goal && profile.goal_mode !== 'general' && (
            <>
              <span className="text-muted-foreground">•</span>
              <span className="text-muted-foreground flex items-center gap-1">
                <Target className="h-3.5 w-3.5" />
                {statusDisplay.goal}
              </span>
            </>
          )}
        </div>

        {/* Current Courses */}
        {showCourseManagement && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-muted-foreground">Current Courses</h4>
              {courses.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-xs text-muted-foreground"
                  onClick={() => clearCourses.mutate()}
                >
                  Clear All
                </Button>
              )}
            </div>

            {coursesLoading ? (
              <Skeleton className="h-8 w-full" />
            ) : courses.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {courses.map((course) => (
                  <Badge
                    key={course.id}
                    variant="outline"
                    className="flex items-center gap-1 pr-1"
                  >
                    {getSubjectLabel(course.subject)}
                    <button
                      onClick={() => removeCourse.mutate(course.id)}
                      className="ml-1 p-2 -mr-1.5 -my-0.5 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1"
                      disabled={removeCourse.isPending}
                      aria-label={`Remove ${getSubjectLabel(course.subject)}`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </Badge>
                ))}
                <Link to="/settings/profile">
                  <Badge
                    variant="outline"
                    className="cursor-pointer hover:bg-muted flex items-center gap-1"
                  >
                    <Plus className="h-3 w-3" />
                    Add
                  </Badge>
                </Link>
              </div>
            ) : (
              <Link to="/settings/profile">
                <Button variant="outline" size="sm" className="w-full">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Your Courses
                </Button>
              </Link>
            )}
          </div>
        )}

        {/* Focus Areas / Weaknesses */}
        {!compact && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-muted-foreground">Focus Areas</h4>

            {weaknessesLoading ? (
              <Skeleton className="h-16 w-full" />
            ) : weaknesses.length > 0 ? (
              <div className="space-y-2">
                {weaknesses.slice(0, 3).map((belief) => (
                  <div
                    key={belief.id}
                    className="flex items-center gap-3 p-2 rounded-lg bg-muted/50"
                  >
                    <div className={cn(
                      "w-2 h-2 rounded-full",
                      belief.confidence > 0.7 ? "bg-red-500 dark:bg-red-400" :
                      belief.confidence > 0.4 ? "bg-yellow-500 dark:bg-yellow-400" :
                      "bg-green-500 dark:bg-green-400"
                    )} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {getSubjectLabel(belief.category)}
                        {belief.subcategory && (
                          <span className="text-muted-foreground font-normal">
                            : {belief.subcategory}
                          </span>
                        )}
                      </p>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {belief.confidence > 0.7 ? 'needs work' :
                       belief.confidence > 0.4 ? 'improving' :
                       'getting there'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-3 text-center bg-muted/30 rounded-lg">
                Practice more to see insights about your strengths and areas for improvement.
              </p>
            )}
          </div>
        )}

        {/* Profile Completeness */}
        {profileCompleteness < 1 && (
          <div className="space-y-1.5 pt-2 border-t">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Profile completeness</span>
              <span className="font-medium">{Math.round(profileCompleteness * 100)}%</span>
            </div>
            <Progress value={profileCompleteness * 100} className="h-1.5" />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ProfileDashboardWidget;
